import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import BusinessOnboardingFlow from '../../components/marketing/onboarding/BusinessOnboardingFlow';
import StrategyDashboard from '../../components/marketing/strategy/StrategyDashboard';
import RoadmapDashboard from '../../components/marketing/roadmap/RoadmapDashboard';
import { businessAPI } from '../../services/businessAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function MarketingToolsPage() {
  const [businessData, setBusinessData] = useState(null);
  const [strategyData, setStrategyData] = useState(null);
  const [roadmapData, setRoadmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBusinessPanel, setShowBusinessPanel] = useState(false);
  const [showStrategyPanel, setShowStrategyPanel] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(true); // default restrictive

  const isBusinessComplete = businessData && businessData.businessName;
  const hasMegaStrategy = strategyData?.megaStrategy?.calendar?.length > 0 && strategyData?.phase2Complete;
  const hasLegacyStrategy = strategyData &&
                             strategyData.smartGoal &&
                             strategyData.smartGoal.fullStatement &&
                             strategyData.proposal &&
                             strategyData.proposal.dailyBreakdown &&
                             strategyData.proposal.dailyBreakdown.length > 0 &&
                             (strategyData.phase2Complete || (strategyData.strategyApproved && strategyData.proposalApproved && strategyData.tab4Complete));
  const isStrategyComplete = hasMegaStrategy || hasLegacyStrategy;

  // Lock body scroll when any panel is open
  useEffect(() => {
    if (showBusinessPanel || showStrategyPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showBusinessPanel, showStrategyPanel]);

  useEffect(() => {
    loadProfileStatus();
  }, []);

  const loadProfileStatus = async () => {
    try {
      setLoading(true);
      const [profileStatusRes, subRes] = await Promise.allSettled([
        businessAPI.getProfileStatus(),
        fetch(`${API_URL}/api/subscription`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => null)
      ]);

      // Subscription check — pro users get all 30 days
      if (subRes.status === 'fulfilled' && subRes.value?.data) {
        const sub = subRes.value.data;
        const isPro = sub.currentTier === 'pro' && sub.status === 'active';
        setIsFreeUser(!isPro);
      }

      const response = profileStatusRes.status === 'fulfilled' ? profileStatusRes.value : null;
      if (response?.success && response?.exists) {
        const profileResponse = await businessAPI.getProfile();
        if (profileResponse.success) {
          setBusinessData(profileResponse.profile);
        }

        const strategyResponse = await businessAPI.getStrategy();
        if (strategyResponse.success && strategyResponse.exists) {
          setStrategyData(strategyResponse.strategy);
        }

        try {
          const roadmapResponse = await businessAPI.getRoadmap();
          if (roadmapResponse.success && roadmapResponse.exists) {
            setRoadmapData(roadmapResponse.roadmap);
          }
        } catch (error) {
          console.log('No roadmap data yet');
        }
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessDataSave = async (data) => {
    setBusinessData(data);
    setShowBusinessPanel(false);
    await loadProfileStatus();
  };

  const handleStrategyDataSave = (data) => {
    setStrategyData(data);
  };

  const handlePhase2Complete = async () => {
    setShowStrategyPanel(false);
    await loadProfileStatus();
  };

  const handleRoadmapDataSave = async (data) => {
    // Optimistic update — apply immediately so CalendarView re-renders right away
    setRoadmapData(data);
    try {
      const response = await businessAPI.saveRoadmap(data);
      if (response.success && response.roadmap) {
        setRoadmapData(response.roadmap);
      }
    } catch (error) {
      console.error('Save roadmap error:', error);
      // State already updated optimistically — UI stays consistent
    }
  };

  const renderContent = () => {
    if (!isBusinessComplete) {
      return (
        <div className="flex items-center justify-center py-32">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-building text-blue-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Business Profile Required</h3>
            <p className="text-sm text-gray-600 mb-6">
              Complete your business profile in Phase 1 to generate your 30-day marketing calendar.
            </p>
            <button onClick={() => setShowBusinessPanel(true)}
              className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all inline-flex items-center gap-2 shadow-lg shadow-blue-600/20">
              <i className="fas fa-building text-xs"></i>
              <span>Setup Business Profile</span>
            </button>
          </div>
        </div>
      );
    }

    if (!isStrategyComplete) {
      return (
        <div className="flex items-center justify-center py-32">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-chess text-green-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Marketing Strategy Required</h3>
            <p className="text-sm text-gray-600 mb-6">
              Define your marketing strategy in Phase 2 to create your personalised content calendar.
            </p>
            <button onClick={() => setShowStrategyPanel(true)}
              className="px-5 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-all inline-flex items-center gap-2 shadow-lg shadow-green-600/20">
              <i className="fas fa-chess text-xs"></i>
              <span>Create Marketing Strategy</span>
            </button>
          </div>
        </div>
      );
    }

    return (
      <RoadmapDashboard
        businessData={businessData}
        strategyData={strategyData}
        roadmapData={roadmapData}
        onRoadmapUpdate={handleRoadmapDataSave}
        isFreeUser={isFreeUser}
      />
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <LoadingSpinner size="lg" text="Loading marketing tools..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
    <DashboardLayout>
      <div className="p-8 space-y-6 bg-gray-50 min-h-screen">

        {/* Header with Phase 1 and Phase 2 cards */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Marketing Studio</h1>
            <p className="text-sm text-gray-500 mt-1 font-medium">AI-powered marketing automation</p>
          </div>

          {/* Phase Cards */}
          <div className="flex flex-col sm:flex-row gap-4">

            {/* PHASE 1 CARD */}
            <div className="group relative bg-white rounded-xl transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden w-full sm:w-72">
              <div className="relative h-12 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-20 h-20 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `linear-gradient(#3b82f6 0.5px, transparent 0.5px), linear-gradient(90deg, #3b82f6 0.5px, transparent 0.5px)`,
                    backgroundSize: '8px 8px'
                  }} className="absolute inset-0"></div>
                  <div style={{ background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)' }} className="absolute inset-0"></div>
                </div>
                <div className="relative z-10 h-full flex items-center justify-between px-4">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Phase 1: Business</p>
                  <div className={`px-2 py-0.5 rounded text-xs font-bold ${isBusinessComplete ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                    {isBusinessComplete ? '✓ Complete' : '● Incomplete'}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-building text-white"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {isBusinessComplete ? businessData.businessName : 'Setup Required'}
                    </p>
                    <p className="text-xs text-gray-500">Business Profile</p>
                  </div>
                </div>
                <button onClick={() => setShowBusinessPanel(true)}
                  className="w-full px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:border-gray-300 transition-all flex items-center justify-center gap-2">
                  <i className="fas fa-pencil text-xs"></i>
                  <span>{isBusinessComplete ? 'Edit Profile' : 'Setup Now'}</span>
                </button>
              </div>
            </div>

            {/* PHASE 2 CARD */}
            <div className="group relative bg-white rounded-xl transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden w-full sm:w-72">
              <div className="relative h-12 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-20 h-20 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `linear-gradient(#16a34a 0.5px, transparent 0.5px), linear-gradient(90deg, #16a34a 0.5px, transparent 0.5px)`,
                    backgroundSize: '8px 8px'
                  }} className="absolute inset-0"></div>
                  <div style={{ background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)' }} className="absolute inset-0"></div>
                </div>
                <div className="relative z-10 h-full flex items-center justify-between px-4">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Phase 2: Strategy</p>
                  <div className={`px-2 py-0.5 rounded text-xs font-bold ${isStrategyComplete ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}`}>
                    {isStrategyComplete ? '✓ Complete' : '○ Pending'}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-chess text-white"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {isStrategyComplete ? 'Strategy Approved' : isBusinessComplete ? 'Ready to Generate' : 'Phase 1 Required'}
                    </p>
                    <p className="text-xs text-gray-500">Marketing Strategy</p>
                  </div>
                </div>
                <button onClick={() => setShowStrategyPanel(true)} disabled={!isBusinessComplete}
                  className={`w-full px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isBusinessComplete
                      ? 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                      : 'bg-gray-100 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
                  }`}>
                  <i className="fas fa-pencil text-xs"></i>
                  <span>{isStrategyComplete ? 'Edit Strategy' : 'Setup Now'}</span>
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Phase 3: Content Calendar */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
          <div className="min-h-[600px]">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Portaled panels rendered via document.body below */}
    </DashboardLayout>

    {/* PHASE 1 PANEL - Portal to document.body */}
    {showBusinessPanel && ReactDOM.createPortal(
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={() => setShowBusinessPanel(false)}></div>
        <div className="fixed right-0 top-0 h-full w-full max-w-5xl bg-white shadow-2xl z-[9999] overflow-y-auto" style={{ animation: 'panelSlideIn 0.3s ease-out' }}>
          <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Phase 1: Business Setup</h2>
                <p className="text-xs text-gray-600 mt-1">Complete your business profile to unlock marketing tools</p>
              </div>
              <button onClick={() => setShowBusinessPanel(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>
          </div>
          <div className="p-6">
            <BusinessOnboardingFlow onSave={handleBusinessDataSave} businessData={businessData} hasExistingCalendar={isStrategyComplete} />
          </div>
        </div>
        <style>{`@keyframes panelSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      </>,
      document.body
    )}

    {/* PHASE 2 PANEL - Portal to document.body */}
    {showStrategyPanel && ReactDOM.createPortal(
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={() => setShowStrategyPanel(false)}></div>
        <div className="fixed right-0 top-0 h-full w-full max-w-5xl bg-white shadow-2xl z-[9999] overflow-y-auto" style={{ animation: 'panelSlideIn 0.3s ease-out' }}>
          <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-6 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Phase 2: Marketing Strategy</h2>
                <p className="text-xs text-gray-600 mt-1">Define your marketing goals and strategies</p>
              </div>
              <button onClick={() => setShowStrategyPanel(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>
          </div>
          <div className="p-6">
            <StrategyDashboard
              businessData={businessData}
              strategyData={strategyData}
              onStrategyUpdate={handleStrategyDataSave}
              onPhaseComplete={handlePhase2Complete}
            />
          </div>
        </div>
        <style>{`@keyframes panelSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      </>,
      document.body
    )}
    </>
  );
}
